'use client';

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import type { BrandMergeGroup } from '@/types/database-schema';
import type { BrandListItem } from './useBrandsList';

export interface BrandDiscoveryTimeSeriesPoint {
  date: string;
  brand: string;
  brand_lowercase: string;
  total_runs: number;
  total_mentioned: number;
  visibility_pct: number;
  rank3_pct: number;
  rank1_pct: number;
  top3_share_pct: number;
  top3_answers_count?: number;
  rank1_answers_count?: number;
  top3_placement_count?: number;
}

interface BrandsPageDiscoveryResponseRow {
  brands?: BrandListItem[];
  time_series?: BrandDiscoveryTimeSeriesPoint[];
}

interface BrandsPageDiscoveryData {
  brands: BrandListItem[];
  timeSeries: BrandDiscoveryTimeSeriesPoint[];
}

interface BrandsPageDiscoveryParams {
  enabled?: boolean;
  job_id?: string;
  job_ids?: string[];
  start_date?: string;
  end_date?: string;
  period_grain?: 'week' | 'month';
  period_key?: string;
  prompt_ids?: string[];
  prompt_group_id?: string;
  prompt_group_origin?: 'local' | 'shared';
  prompt_subtopic_id?: string;
  engine?: string;
  brand_groups?: BrandMergeGroup[];
  limit?: number;
}

const DEFAULT_DISCOVERY_LIMIT = 200;
const EMPTY_DISCOVERY_PARAMS: BrandsPageDiscoveryParams = {};
const EMPTY_DATA: BrandsPageDiscoveryData = {
  brands: [],
  timeSeries: [],
};

const brandsPageDiscoveryCache = new Map<
  string,
  { data: BrandsPageDiscoveryData; timestamp: number }
>();
const brandsPageDiscoveryRequests = new Map<string, Promise<BrandsPageDiscoveryData>>();
const CACHE_DURATION_MS = 60 * 60 * 1000;
const BRANDS_PAGE_DISCOVERY_CACHE_SCHEMA_VERSION = 'brands-page-discovery-v2';

function normalizeStringList(values?: string[]): string[] | undefined {
  if (!values?.length) return undefined;
  const normalized = [
    ...new Set(values.map(value => String(value || '').trim()).filter(Boolean)),
  ].sort();
  return normalized.length > 0 ? normalized : undefined;
}

function normalizeBrandGroups(groups?: BrandMergeGroup[]): BrandMergeGroup[] | undefined {
  if (!groups?.length) return undefined;
  const normalized = groups
    .map(group => ({
      canonical_name: String(group.canonical_name || '').trim(),
      variations: normalizeStringList(group.variations) || [],
    }))
    .filter(group => group.canonical_name)
    .sort((left, right) => left.canonical_name.localeCompare(right.canonical_name));
  return normalized.length > 0 ? normalized : undefined;
}

function getCacheKey(projectId: string, params: BrandsPageDiscoveryParams): string {
  const parts = [
    projectId,
    `${params.prompt_group_origin || 'local'}:${params.prompt_group_id || ''}:${params.prompt_subtopic_id || ''}`,
  ];
  if (params.period_grain && params.period_key) {
    parts.push(`period:${params.period_grain}:${params.period_key}`);
  } else if (params.start_date && params.end_date) {
    parts.push(`range:${params.start_date}:${params.end_date}`);
  } else if (params.job_ids?.length) {
    parts.push(`jobs:${[...params.job_ids].sort().join(',')}`);
  } else if (params.job_id) {
    parts.push(`job:${params.job_id}`);
  }
  if (params.prompt_ids?.length) {
    parts.push(`prompts:${[...params.prompt_ids].sort().join(',')}`);
  }
  if (params.engine) {
    parts.push(`engine:${params.engine}`);
  }
  if (params.brand_groups?.length) {
    parts.push(`groups:${JSON.stringify(params.brand_groups)}`);
  }
  parts.push(`limit:${params.limit || DEFAULT_DISCOVERY_LIMIT}`);
  parts.push(`schema:${BRANDS_PAGE_DISCOVERY_CACHE_SCHEMA_VERSION}`);
  return parts.join('|');
}

export function useBrandsPageDiscovery(params: BrandsPageDiscoveryParams = EMPTY_DISCOVERY_PARAMS) {
  const { user } = useAuth();
  const { currentProject, loading: projectLoading, isTransitioning, isReady } = useProject();

  const projectId = currentProject?.id || null;
  const enabled = params.enabled !== false;
  const jobIdsKey = JSON.stringify(normalizeStringList(params.job_ids) || []);
  const promptIdsKey = JSON.stringify(normalizeStringList(params.prompt_ids) || []);
  const brandGroupsKey = JSON.stringify(normalizeBrandGroups(params.brand_groups) || []);
  const normalizedParams = useMemo<BrandsPageDiscoveryParams>(() => {
    const jobIds = JSON.parse(jobIdsKey) as string[];
    const promptIds = JSON.parse(promptIdsKey) as string[];
    const brandGroups = JSON.parse(brandGroupsKey) as BrandMergeGroup[];
    return {
      job_id: params.job_id,
      job_ids: jobIds.length > 0 ? jobIds : undefined,
      start_date: params.start_date,
      end_date: params.end_date,
      period_grain: params.period_grain,
      period_key: params.period_key,
      prompt_group_id: params.prompt_group_id,
      prompt_group_origin: params.prompt_group_origin,
      prompt_subtopic_id: params.prompt_subtopic_id,
      prompt_ids: promptIds.length > 0 ? promptIds : undefined,
      engine: params.engine,
      brand_groups: brandGroups.length > 0 ? brandGroups : undefined,
      limit: params.limit || DEFAULT_DISCOVERY_LIMIT,
    };
  }, [
    brandGroupsKey,
    jobIdsKey,
    params.prompt_group_id,
    params.prompt_group_origin,
    params.prompt_subtopic_id,
    params.end_date,
    params.engine,
    params.job_id,
    params.limit,
    params.period_grain,
    params.period_key,
    params.start_date,
    promptIdsKey,
  ]);
  const activeRequestVersionRef = useRef(0);
  const dataCacheKeyRef = useRef<string | null>(null);

  const cacheKey = useMemo(() => {
    if (!enabled || !projectId) return null;
    return `${currentProject?.topic_options_revision || currentProject?.prompt_topics_revision || ''}|${user?.uid || 'signed-out'}|${getCacheKey(projectId, normalizedParams)}`;
  }, [
    enabled,
    normalizedParams,
    projectId,
    user?.uid,
    currentProject?.topic_options_revision || currentProject?.prompt_topics_revision,
  ]);

  const [data, setData] = useState<BrandsPageDiscoveryData>(() => {
    if (!cacheKey) return EMPTY_DATA;
    const cached = brandsPageDiscoveryCache.get(cacheKey);
    if (cached?.data) {
      dataCacheKeyRef.current = cacheKey;
      return cached.data;
    }
    return EMPTY_DATA;
  });
  const [loading, setLoading] = useState(
    Boolean(enabled && (!cacheKey || !brandsPageDiscoveryCache.has(cacheKey)))
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCacheKey, setErrorCacheKey] = useState<string | null>(null);
  const activeCacheKeyRef = useRef(cacheKey);
  const activeProjectScopeRef = useRef({ projectId, userId: user?.uid });
  useLayoutEffect(() => {
    // Suspended renders must not invalidate requests for the committed UI.
    activeCacheKeyRef.current = cacheKey;
    activeProjectScopeRef.current = { projectId, userId: user?.uid };
  }, [cacheKey, projectId, user?.uid]);
  useEffect(
    () => () => {
      activeRequestVersionRef.current += 1;
    },
    []
  );
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const fetchBrandsPageDiscovery = useCallback(
    async (
      targetCacheKey: string,
      targetProjectId: string,
      force = false
    ): Promise<BrandsPageDiscoveryData> => {
      if (!user || !targetProjectId || !targetCacheKey) {
        return EMPTY_DATA;
      }

      const cached = brandsPageDiscoveryCache.get(targetCacheKey);
      if (
        !normalizedParams.prompt_group_id &&
        !force &&
        cached &&
        Date.now() - cached.timestamp < CACHE_DURATION_MS
      ) {
        return cached.data;
      }
      const pending = brandsPageDiscoveryRequests.get(targetCacheKey);
      if (!force && pending) return pending;

      const request = (async () => {
        const token = await user.getIdToken();
        // Ignore an old project after token restoration, while allowing shared
        // requests that other consumers still need after a filter change.
        if (
          activeProjectScopeRef.current.projectId !== targetProjectId ||
          activeProjectScopeRef.current.userId !== user.uid
        )
          return EMPTY_DATA;
        const requestParams: Record<string, unknown> = {
          project_id: targetProjectId,
          limit: normalizedParams.limit,
          engine: normalizedParams.engine,
        };

        if (normalizedParams.period_grain && normalizedParams.period_key) {
          requestParams.period_grain = normalizedParams.period_grain;
          requestParams.period_key = normalizedParams.period_key;
        } else if (normalizedParams.start_date && normalizedParams.end_date) {
          requestParams.start_date = normalizedParams.start_date;
          requestParams.end_date = normalizedParams.end_date;
        } else if (normalizedParams.job_ids?.length) {
          requestParams.job_ids = normalizedParams.job_ids;
        } else if (normalizedParams.job_id) {
          requestParams.job_id = normalizedParams.job_id;
        }

        if (normalizedParams.prompt_group_id)
          requestParams.prompt_group_id = normalizedParams.prompt_group_id;
        if (normalizedParams.prompt_group_origin === 'shared')
          requestParams.prompt_group_origin = 'shared';
        if (normalizedParams.prompt_subtopic_id)
          requestParams.prompt_subtopic_id = normalizedParams.prompt_subtopic_id;
        if (normalizedParams.prompt_ids?.length) {
          requestParams.prompt_ids = normalizedParams.prompt_ids;
        }

        if (normalizedParams.brand_groups?.length) {
          requestParams.brand_groups = normalizedParams.brand_groups;
        }

        const response = await fetch('/api/pipeline-ui', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            query_type: 'brands_page_discovery_fast',
            params: requestParams,
          }),
        });

        if (!response.ok) {
          let serverMessage = '';
          try {
            const errorPayload = await response.json();
            const detailParts = [
              typeof errorPayload?.error === 'string' ? errorPayload.error : '',
              typeof errorPayload?.details === 'string' ? errorPayload.details : '',
            ].filter(Boolean);
            serverMessage = detailParts.join(': ');
          } catch {
            // Ignore JSON parse errors and keep the fallback message.
          }
          throw Object.assign(
            new Error(serverMessage || 'Failed to fetch brands page discovery data'),
            { status: response.status }
          );
        }

        const result = await response.json();
        const row = (result.data?.[0] || {}) as BrandsPageDiscoveryResponseRow;
        const nextData: BrandsPageDiscoveryData = {
          brands: Array.isArray(row.brands) ? row.brands : [],
          timeSeries: Array.isArray(row.time_series) ? row.time_series : [],
        };

        brandsPageDiscoveryCache.set(targetCacheKey, { data: nextData, timestamp: Date.now() });
        return nextData;
      })();
      if (force) return request;
      brandsPageDiscoveryRequests.set(targetCacheKey, request);
      return request.finally(() => {
        if (brandsPageDiscoveryRequests.get(targetCacheKey) === request) {
          brandsPageDiscoveryRequests.delete(targetCacheKey);
        }
      });
    },
    [normalizedParams, user]
  );

  useEffect(() => {
    const requestVersion = ++activeRequestVersionRef.current;
    let cancelled = false;
    const isLatestRequest = () =>
      !cancelled &&
      activeRequestVersionRef.current === requestVersion &&
      activeCacheKeyRef.current === cacheKey;

    if (!enabled) {
      if (isLatestRequest()) {
        setData(EMPTY_DATA);
        dataCacheKeyRef.current = null;
        setLoading(false);
        setIsRefreshing(false);
        setError(null);
        setErrorStatus(null);
      }
      return () => {
        cancelled = true;
      };
    }

    if (!user || projectLoading || !isReady) {
      return () => {
        cancelled = true;
      };
    }

    if (!cacheKey || !projectId) {
      if (!isTransitioning && isLatestRequest()) {
        setData(EMPTY_DATA);
        dataCacheKeyRef.current = null;
      }
      if (isLatestRequest()) {
        setLoading(false);
        setIsRefreshing(false);
      }
      return () => {
        cancelled = true;
      };
    }

    const cached = brandsPageDiscoveryCache.get(cacheKey);
    if (
      !normalizedParams.prompt_group_id &&
      cached &&
      Date.now() - cached.timestamp < CACHE_DURATION_MS
    ) {
      if (isLatestRequest()) {
        setData(cached.data);
        dataCacheKeyRef.current = cacheKey;
        setError(null);
        setErrorStatus(null);
        setLoading(false);
        setIsRefreshing(false);
      }
      return () => {
        cancelled = true;
      };
    }

    if (isLatestRequest()) {
      const hasCurrentData = dataCacheKeyRef.current === cacheKey;
      setLoading(!hasCurrentData);
      setIsRefreshing(hasCurrentData);
    }
    if (isLatestRequest()) {
      setError(null);
      setErrorStatus(null);
    }

    fetchBrandsPageDiscovery(cacheKey, projectId)
      .then(nextData => {
        if (!isLatestRequest()) return;
        setData(nextData);
        dataCacheKeyRef.current = cacheKey;
      })
      .catch(err => {
        if (!isLatestRequest()) return;
        console.error('Failed to load brands page discovery data:', err);
        setError(err.message);
        setErrorCacheKey(cacheKey);
        setErrorStatus(typeof err.status === 'number' ? err.status : null);
      })
      .finally(() => {
        if (!isLatestRequest()) return;
        setLoading(false);
        setIsRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    user,
    enabled,
    projectLoading,
    isReady,
    cacheKey,
    projectId,
    isTransitioning,
    fetchBrandsPageDiscovery,
  ]);

  const refresh = useCallback(async () => {
    if (!enabled || !user || projectLoading || !isReady || !cacheKey || !projectId) return;
    const requestVersion = ++activeRequestVersionRef.current;
    const isCurrentRequest = () =>
      requestVersion === activeRequestVersionRef.current && activeCacheKeyRef.current === cacheKey;

    const hasCurrentData = dataCacheKeyRef.current === cacheKey;
    setLoading(!hasCurrentData);
    setIsRefreshing(hasCurrentData);
    setError(null);
    setErrorStatus(null);
    try {
      const nextData = await fetchBrandsPageDiscovery(cacheKey, projectId, true);
      if (!isCurrentRequest()) return;
      setData(nextData);
      dataCacheKeyRef.current = cacheKey;
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(err instanceof Error ? err.message : 'Failed to refresh');
      setErrorCacheKey(cacheKey);
      setErrorStatus(
        typeof (err as { status?: unknown })?.status === 'number'
          ? (err as { status: number }).status
          : null
      );
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [enabled, user, projectLoading, isReady, fetchBrandsPageDiscovery, cacheKey, projectId]);

  const cachedCurrentData = cacheKey ? brandsPageDiscoveryCache.get(cacheKey)?.data : undefined;
  const hasCurrentData = dataCacheKeyRef.current === cacheKey || Boolean(cachedCurrentData);
  const visibleData =
    dataCacheKeyRef.current === cacheKey ? data : (cachedCurrentData ?? EMPTY_DATA);
  const currentError = cacheKey && errorCacheKey === cacheKey ? error : null;
  const awaitingCurrentData = Boolean(enabled && cacheKey && !hasCurrentData && !currentError);
  const initialLoading =
    enabled && (loading || awaitingCurrentData || (projectLoading && !hasCurrentData));

  return {
    brands: visibleData.brands,
    timeSeries: visibleData.timeSeries,
    loading: initialLoading,
    initialLoading,
    isRefreshing: isRefreshing || (projectLoading && hasCurrentData),
    error: currentError,
    errorStatus: currentError ? errorStatus : null,
    refresh,
  };
}
